/**
 * A looping, self-contained animation of what an FDS actually does, for a visitor who has
 * never heard the term.
 *
 * The metaphor is deliberately concrete: transactions arrive like people at an airport
 * security line. Most walk straight through (APPROVE); a few set off the scanner and get
 * pulled aside for a person to check (REVIEW). The point the motion makes, which a static
 * diagram cannot, is the RATIO - almost everything flows through untouched, and only a thin
 * stream peels off to a human. That is the entire product in one picture.
 *
 * Pure CSS animation, no library, no canvas. Twelve transaction tokens are staggered along
 * one keyframed path; ten take the "approved" branch and two take the "review" branch, so the
 * split a visitor sees matches the story the rest of the page tells. Honouring
 * prefers-reduced-motion, the tokens are shown at rest in their final positions instead of
 * moving - the layout still reads as "many through, few aside".
 */

const APPROVE_COUNT = 10;
const REVIEW_COUNT = 2;

export function HowItWorksAnimation() {
  const approveTokens = Array.from({ length: APPROVE_COUNT }, (_, i) => i);
  const reviewTokens = Array.from({ length: REVIEW_COUNT }, (_, i) => i);

  return (
    <div className="hiw" aria-hidden="true">
      <div className="hiw-stage">
        {/* Source: transactions arriving */}
        <div className="hiw-source">
          <span className="hiw-source-icon">💸</span>
          <span className="hiw-col-label">송금 요청</span>
        </div>

        {/* The scanner: the FDS engine */}
        <div className="hiw-scanner">
          <div className="hiw-scanner-glow" />
          <span className="hiw-scanner-title">SafeFDS</span>
          <span className="hiw-scanner-sub">실시간 위험 판정</span>
        </div>

        {/* Two exits */}
        <div className="hiw-exits">
          <div className="hiw-exit approve">
            <span className="hiw-exit-title">바로 통과</span>
            <span className="hiw-exit-sub">사람 손 안 탐</span>
          </div>
          <div className="hiw-exit review">
            <span className="hiw-exit-title">사람이 검토</span>
            <span className="hiw-exit-sub">의심스러운 건만</span>
          </div>
        </div>

        {/* Moving transaction tokens. Each token's delay staggers the stream. Hidden when the
            visitor prefers reduced motion; the static dots below take over. */}
        <div className="hiw-tokens">
          {approveTokens.map((i) => (
            <span
              key={`a${i}`}
              className="hiw-token to-approve"
              style={{ animationDelay: `${i * 0.6}s` }}
            />
          ))}
          {reviewTokens.map((i) => (
            <span
              key={`r${i}`}
              className="hiw-token to-review"
              style={{ animationDelay: `${2.4 + i * 3}s` }}
            />
          ))}
        </div>

        {/* Static fallback for prefers-reduced-motion: same 10-to-2 split, held still. */}
        <div className="hiw-rest">
          <div className="hiw-rest-group approve">
            {approveTokens.map((i) => (
              <span key={`ra${i}`} className="hiw-dot approve" />
            ))}
          </div>
          <div className="hiw-rest-group review">
            {reviewTokens.map((i) => (
              <span key={`rr${i}`} className="hiw-dot review" />
            ))}
          </div>
        </div>
      </div>

      <p className="hiw-caption">
        FDS의 목표: 안전한 거래는 <strong className="approve-text">바로 통과</strong>시키고,
        위험 신호가 있는 거래만 <strong className="review-text">사람에게</strong> 넘기는 것.
        위 그림은 개념 설명이며, <strong>이 시스템이 실제로 어디까지 도달했는지는</strong> 아래
        측정 결과에 정직하게 나와 있습니다.
      </p>
    </div>
  );
}
